import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { ClickOutsideDirectiveModule } from '@commons/directives/click-outside/click-outside.directive';
import { PressEscapeKeyDirectiveModule } from '@commons/directives/press-escape-key/press-escape-key.directive';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import {
  SearchEngineFieldSelectDuration,
  SearchEngineFieldSelectDurationValue,
} from '@domains/search-engine/entities/select-duration-field';
import { SearchEngineQueryParams } from '@domains/search-engine/search-engine-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-select-duration-pole-emploi',
  templateUrl: './select-duration.component.html',
  styleUrls: ['./select-duration.component.scss', './select-duration-search-fields.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectDurationComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectDurationComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() field!: SearchEngineFieldSelectDuration;
  @Input() modalOpen = false;
  @Input() isInMainFields = false;
  @Input() liveSearch = false;

  valueToDisplay$ = new BehaviorSubject<string | undefined>(undefined);
  showPlaceholder$ = new BehaviorSubject<boolean>(true);
  showDropdown$ = new BehaviorSubject<boolean>(false);
  showInputFields$ = new BehaviorSubject<boolean>(false);
  valueChanges$ = new Observable<any>();

  @Output() hasChanged = new EventEmitter<boolean>();
  @Output() isPanelOpen = new EventEmitter<boolean>();

  searchFromIsFocused = false;
  searchToIsFocused = false;

  selectForm?: FormGroup;

  private _value?: Record<string, string | number> | null;

  set value(value: Record<string, string | number> | null) {
    if (this._value === value) {
      return;
    }

    this._value = value;

    this.onChanged(value);
    this.onTouched();
  }

  get value(): Record<string, string | number> | null {
    if (this._value === undefined) {
      return null;
    }
    return this._value;
  }

  get from(): AbstractControl | null | undefined {
    return this.selectForm?.get('from');
  }

  get to(): AbstractControl | null | undefined {
    return this.selectForm?.get('to');
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly breakpointsService: BreakpointsService
  ) {}

  ngOnInit(): void {
    this.selectForm = this.formBuilder.group({
      [this.field.name]: undefined,
      from: undefined,
      to: undefined,
    });

    this.route.queryParamMap.pipe(take(1)).subscribe((q) => {
      if (this.field?.name) {
        this.selectForm?.patchValue({
          [this.field.name]: q.get(this.field.name),
          from: Number.parseInt(q.get(SearchEngineQueryParams.From) ?? '', 10),
          to: Number.parseInt(q.get(SearchEngineQueryParams.To) ?? '', 10),
        });

        if (q.get(this.field.name)) {
          const fieldValue = this.field.value?.find((item) => item.paramValue === q.get(this.field.name));
          this.showInputFields$.next(!!fieldValue?.showInputFields);

          this.valueToDisplay$.next(fieldValue?.label);
          this.showPlaceholder$.next(false);
        }

        this.cdr.detectChanges();
      }
    });

    if (this.selectForm) {
      this.selectForm.valueChanges
        .pipe(
          untilDestroyed(this),
          tap((value: Record<string, string>) => {
            this.value = value;
          }),
          switchMap((_) => this.isMobile$),
          filter((isMobile: boolean) => isMobile),
          map((_) => this.showDropdown$.next(false))
        )
        .subscribe();
    }
  }

  selectValue(value: SearchEngineFieldSelectDurationValue): void {
    if (value.showInputFields) {
      this.showInputFields$.next(true);
    } else {
      this.showDropdown$.next(false);
      this.showInputFields$.next(false);
      this.from?.patchValue(undefined);
      this.to?.patchValue(undefined);
    }

    this.selectForm?.get(this.field.name)?.patchValue(value.paramValue);
    this.valueToDisplay$.next(value.label);
    this.showPlaceholder$.next(false);

    this.hasChanged.emit();
    this.isPanelOpen.emit(this.showDropdown$.value);
  }

  openCloseDropdown(): void {
    this.showDropdown$.pipe(untilDestroyed(this), take(1)).subscribe({
      next: (showDropdown: boolean) => {
        this.showDropdown$.next(!showDropdown);
        this.isPanelOpen.emit(this.showDropdown$.value);
      },
    });
  }

  closeDropdown(): void {
    this.showDropdown$.pipe(untilDestroyed(this), take(1)).subscribe({
      next: (showDropdown: boolean) => {
        if (showDropdown) {
          this.showDropdown$.next(false);
          this.isPanelOpen.emit(false);
        }
      },
    });
  }

  onChanged: (_: Record<string, string | number> | null) => void = (_: Record<string, string | number> | null) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  registerOnChange(fn: (value: Record<string, string | number> | null) => void): void {
    this.onChanged = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  writeValue = (value: Record<string, string | number> | null): Record<string, string | number> | null =>
    (this.value = value);

  clearValue(): void {
    this.selectForm?.get(this.field.name)?.setValue(undefined);

    this.value = null;
    this.showInputFields$.next(false);
    this.showPlaceholder$.next(true);
    this.hasChanged.emit();
  }

  trackByDurationSelectSearchValue(_: number, selectSearchValue: SearchEngineFieldSelectDurationValue): string | null {
    return selectSearchValue?.label;
  }

  ngOnDestroy(): void {
    this.showInputFields$.complete();
    this.valueToDisplay$.complete();
    this.showDropdown$.complete();
    this.showPlaceholder$.complete();
  }
}

@NgModule({
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatInputModule,
    ClickOutsideDirectiveModule,
    NgUpperFirstPipeModule,
    A11yModule,
    PressEscapeKeyDirectiveModule,
  ],
  declarations: [SelectDurationComponent],
  exports: [SelectDurationComponent],
})
export class SelectDurationModule {}
